import { useEffect, useState } from 'react';
import { createSubscription } from '@genfun/core/api/auth';
import { openTabWithUrl } from '@genfun/core/utils/openTabWithUrl';

// position:fixed lock — overflow:hidden alone doesn't stop touch scroll on iOS Safari
function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollY = window.scrollY;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            overflow: body.style.overflow,
            paddingRight: body.style.paddingRight,
        };
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.left = prev.left;
            body.style.right = prev.right;
            body.style.overflow = prev.overflow;
            body.style.paddingRight = prev.paddingRight;
            window.scrollTo({ top: scrollY, behavior: 'instant' });
        };
    }, [isOpen]);
}

// Payment-method badges for the popup footer. Inline SVG so the wordmarks render
// crisp on any background without extra assets.
const AppleLogo = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z"/>
    </svg>
);
const GoogleLogo = (
    <svg viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
);
const CardIcon = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="2" y="9" width="20" height="3" fill="currentColor"/>
        <rect x="5" y="14.5" width="6" height="1.8" rx="0.9" fill="currentColor"/>
    </svg>
);

export function PricingPopup({ isOpen, onClose, token, user, overlayClassName = 'popupOverlay' }) {
    useLockBodyScroll(isOpen);
    const [loading, setLoading] = useState(null);

    const handleBuy = (tier) => {
        if (!token || loading) return;
        setLoading(tier);
        openTabWithUrl(() => createSubscription(token, tier).then(r => r.url))
            .catch(e => console.error('Subscription error:', e))
            .finally(() => setLoading(null));
    };

    if (!isOpen) return null;
    return (
        <div className={overlayClassName} onClick={onClose}>
            <div className="pricingPopup" onClick={e => e.stopPropagation()}>
                <button className="pricingClose" onClick={onClose}>&times;</button>
                <div className="pricingHeader">
                    <h2 className="pricingTitle">Unlock unlimited fantasy</h2>
                    <p className="pricingSubtitle">Cancel anytime · No watermark</p>
                    {user && (
                        <div className="pricingBalance">
                            Your balance: <b>{user.credits ?? 0}</b> credits
                        </div>
                    )}
                </div>
                <div className="pricingCards">
                    <div className="pricingCard">
                        <div className="pricingCardName">Starter</div>
                        <div className="pricingCardPriceRow">
                            <span className="pricingCardPrice">$9.99</span>
                            <span className="pricingPer">/w</span>
                        </div>
                        <ul className="pricingFeatures">
                            <li>50 photo generations</li>
                            <li>10 video generations</li>
                            <li>HD quality</li>
                            <li>Cancel anytime</li>
                        </ul>
                        <button className="pricingBuyBtn" onClick={() => handleBuy(1)} disabled={!!loading}>
                            {loading === 1 ? 'Redirecting…' : 'Buy'}
                        </button>
                    </div>
                    <div className="pricingCard pricingCard--popular">
                        <div className="pricingBadge">Most popular · Save 75%</div>
                        <div className="pricingCardName">Premium</div>
                        <div className="pricingCardPriceRow">
                            <span className="pricingCardPrice">$19.99</span>
                            <span className="pricingPer">/w</span>
                            <span className="pricingOldPrice">$60</span>
                        </div>
                        <ul className="pricingFeatures">
                            <li>400 photo generations</li>
                            <li>20 video generations</li>
                            <li>Priority queue</li>
                            <li>Full HD quality</li>
                        </ul>
                        <button className="pricingBuyBtn pricingBuyBtn--primary" onClick={() => handleBuy(2)} disabled={!!loading}>
                            {loading === 2 ? 'Redirecting…' : 'Buy'}
                        </button>
                    </div>
                </div>
                <div className="pricingFooter">
                    <div className="pricingPayRow">
                        <span className="pricingPayBadge pricingPayBadge--apple" aria-label="Apple Pay">
                            {AppleLogo}<span>Pay</span>
                        </span>
                        <span className="pricingPayBadge pricingPayBadge--google" aria-label="Google Pay">
                            {GoogleLogo}<span>Pay</span>
                        </span>
                        <span className="pricingPayBadge pricingPayBadge--card" aria-label="Card">
                            {CardIcon}<span>Card</span>
                        </span>
                    </div>
                    <div className="pricingFooterNote">🔒 Secure checkout</div>
                </div>
            </div>
        </div>
    );
}
